import { Box, Button, Flex, IconButton, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import { CSVLink } from "react-csv";
import { MdWarningAmber } from "react-icons/md";
import { brandPalette } from "../../../constants/brandPalette";
import CTable from "../../../components/Table/table";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { unpaidReportRequest } from "../../../modules/admin-modules/reports/Actions";
import { getPaymentSubjectRequest } from "../../../modules/admin-modules/videos/Actions";
import { getAllGroupsRequest } from "../../../modules/admin-modules/groups/Actions";
import { deleteStudentRequest } from "../../../modules/admin-modules/students/Actions";

const selectBase = adminSelectStyles({ menuZIndex: 1600 });
const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};
const cardSx = {
  border: "1px solid",
  borderColor: "var(--border-subtle)",
  bg: "var(--surface-raised)",
  borderRadius: "18px",
  boxShadow: "0 18px 55px rgba(0,0,0,0.12)",
  color: "var(--text-primary)",
};

const headers = [
  { label: "name", key: "name" },
  { label: "group_name", key: "group_name" },
  { label: "phone", key: "phone" },
  { label: "parent_phone", key: "parent_phone" },
  { label: "last_attendance_date", key: "last_attendance_date" },
];

const UnpaiedReport = () => {
  const dispatch = useDispatch();
  const [paymentId, setPaymentId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [size, setPerPage] = useState(10);
  const setPage = () => {};

  useEffect(() => {
    if (paymentId) dispatch(unpaidReportRequest(groupId, paymentId));
  }, [groupId, paymentId, dispatch]);

  useEffect(() => {
    dispatch(getPaymentSubjectRequest());
    dispatch(getAllGroupsRequest());
  }, [dispatch]);

  const paymentSubjects = useSelector((state) => state.videos.paymentSubjects);
  const groups = useSelector((state) => state.groups.groups);
  const unpaidReport = useSelector((state) => state.reports.unpaidReport);
  const isLoading = useSelector((state) => state.reports.isLoading);

  const paymentSubjectsOptions = useMemo(
    () =>
      (paymentSubjects || []).map((data) => ({
        value: data.id,
        label: data.name,
      })),
    [paymentSubjects]
  );

  const groupsOptions = useMemo(
    () =>
      (groups || []).map((data) => ({
        value: data.id,
        label: data.name,
      })),
    [groups]
  );

  const Data = useMemo(
    () =>
      (unpaidReport || []).map((data, index) => ({
        ...data,
        index: `#${index + 1}`,
        id: data?.id,
        name: data?.name,
        group_name: data?.group?.name,
        phone: data?.phone,
        parent_phone: data?.parent_phone,
        last_attendance_date: data?.last_attendance_date,
        delete: (
          <IconButton
            variant="ghost"
            aria-label="Deactivate student"
            fontSize="20px"
            borderRadius="lg"
            icon={
              <MdWarningAmber
                color={data?.deleted_at == null ? "#9B2C2C" : "#276749"}
              />
            }
            onClick={() => dispatch(deleteStudentRequest(data?.id))}
            isLoading={isLoading}
          />
        ),
      })),
    [unpaidReport, isLoading, dispatch]
  );

  const columns = [
    { Header: "#", accessor: "index" },
    { Header: "Student", accessor: "name" },
    { Header: "Group", accessor: "group_name" },
    { Header: "Parent phone", accessor: "parent_phone" },
    { Header: "Phone", accessor: "phone" },
    { Header: "Last attendance", accessor: "last_attendance_date" },
    { Header: "Deactivate", accessor: "delete" },
  ];

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={6}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Unpaid report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Students who have not paid for the selected payment subject.
            </Text>
          </Box>
          <LogoutMenu />
        </Flex>

        <Flex gap={4} mb={6} flexWrap="wrap" align={{ base: "stretch", md: "flex-end" }}>
          <Box flex="1" minW={{ base: "100%", md: "220px" }} maxW={{ md: "320px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Group
            </Text>
            <ReactSelect
              options={groupsOptions}
              isMulti={false}
              onChange={(opt) => setGroupId(opt?.value ?? "")}
              styles={selectStyles}
              placeholder="Select group"
              value={groupsOptions.find((o) => o.value === groupId) || null}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              isClearable
            />
          </Box>
          <Box flex="1" minW={{ base: "100%", md: "220px" }} maxW={{ md: "320px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Payment subject
            </Text>
            <ReactSelect
              options={paymentSubjectsOptions}
              isMulti={false}
              onChange={(opt) => setPaymentId(opt?.value ?? "")}
              styles={selectStyles}
              placeholder="Select payment"
              value={paymentSubjectsOptions.find((o) => o.value === paymentId) || null}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              isClearable
            />
          </Box>
          {Data.length > 0 ? (
            <CSVLink
              headers={headers}
              data={Data}
              filename="unpaid-report.csv"
              target="_blank"
            >
              <Button
                size="sm"
                h="44px"
                borderRadius="lg"
                bg={brandPalette.secondary}
                color="white"
                fontWeight="800"
                px={6}
                _hover={{ bg: brandPalette.primary }}
              >
                Export CSV
              </Button>
            </CSVLink>
          ) : null}
        </Flex>

        <Box {...cardSx} p={{ base: 4, md: 6 }}>
          {!paymentId ? (
            <Text fontWeight="600" color="var(--text-muted)">
              Select a payment subject to load unpaid students.
            </Text>
          ) : (
            <CTable
              selectedData={() => {}}
              Columns={columns}
              Data={Data}
              Actions
              currentpage={pageNumber}
              setPage={setPage}
              setPageNumber={setPageNumber}
              setPerPage={setPerPage}
              totalPage={Math.ceil((Data.length || 0) / size) || 1}
              isLoading={isLoading}
              perPage={size}
              noSearchBar
              noFilter
              noSecondSearchBar
              wrapperProps={{
                borderWidth: "1px",
                borderColor: "var(--border-subtle)",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            />
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default UnpaiedReport;
